import _WidgetBaseSP from 'more/_WidgetBaseSP';
import lang from 'dojo/_base/lang';
import { compose, mdsWidget } from 'dojo/_base/declare';
import template from './ExpandableSection.html?raw';
import on from 'dojo/on';
import { HTMLElementWithExpander, ExpandableSectionMarkings, _WidgetBaseSPWithExpander } from "./ExpandableSectionTypes";
import _Container from 'dijit/_Container';
import _WidgetBase from 'dijit/_WidgetBase';
import _Widget from 'dijit/_Widget';
import { AccessibilityLabelsHelper, HtmlHelpers, TranslationHelpers } from "../../../more/Helpers";
import methodHooks from 'more/mdsMethodHooks';



interface TargetNodeConfig {
    node: HTMLElement,
    useInnerContent: boolean,
    useAsTargetNode: boolean
};
class ExpandableSection extends methodHooks(compose(_WidgetBaseSP, _Container)) {

    templateString: any = template;

    private progressContainer: HTMLElement;
    private headerContainer: HTMLElement;
    private headingTxtL: HTMLElement;
    private headingTxtM: HTMLElement;
    private headingTxtS: HTMLElement;
    private expanderIconL: HTMLElement;
    private expanderIconM: HTMLElement;
    private expanderIconS: HTMLElement;
    private counterNode: HTMLElement;
    private headerElementsContainer: HTMLElement;
    private iconL: HTMLElement;
    private iconM: HTMLElement;
    private iconS: HTMLElement;
    private iconContainer: HTMLElement;
    private outerContainer: HTMLElement;

    private currentHeadingIcons: Array<HTMLElement>;


    private _cyText: string = "";

    private _isExpanded: boolean = false;
    private _isExpandable: boolean = true;
    private _isCollapsible: boolean = true;

    private _animationSpeed: number = 500; // todo: es müsst aus dem css das animationDuration-Property ausgelesen werden, an dem die Animationsgeschwindigkeit hängt... ein bisschen ein zu hoher Wert sollte aber nicht schaden im Zweifel.
    /**
 * Konfiguriert einen neuen Expandable Container mit Inhalt und returniert die Node des Containers.
 * Der Expander übernimmt den Inhalt von containerElement und überschreibt den Verweise auf das HTML-Element mit dem eigenen Content-Node.
 * @param containerElement Container dessen Inhalt in den Expander übersiedelt wird. Die Node wir anschließend mit der Content-Node des Expanders überschrieben.
 * @param heading
 * @param size
 * @param expandable
 * @param collapsible
 * @param expanded
 */
    setupAsReplacement(containerElement: HTMLElement,
        heading: HTMLElement | string,
        size?: "large" | "medium" | "small",
        expandable?: boolean,
        collapsible?: boolean,
        expanded?: boolean,
        marking?: ExpandableSectionMarkings): HTMLElementWithExpander {

        let config: TargetNodeConfig = {
            node: containerElement,
            useAsTargetNode: true,
            useInnerContent: true
        };

        let node = this.setup(
            <any>config,
            heading,
            size,
            expandable,
            collapsible,
            expanded,
            marking
        ) as HTMLElementWithExpander;


        node.expander = this;

        return node;
    }

    /**
     * Konfiguriert einen neuen Expandable Container mit Inhalt und returniert die Node des Containers.
     * @param content Falls content ein HTML-Element ist platziert sich die Section anstelle dieser und integriert die Node.
     * @param heading
     * @param size
     * @param expandable
     * @param collapsible
     * @param expanded
     */
    setup(content: HTMLElement | string,
        heading: HTMLElement | string,
        size?: "large" | "medium" | "small",
        expandable?: boolean,
        collapsible?: boolean,
        expanded?: boolean,
        marking?: ExpandableSectionMarkings,
        iconClass?: string): HTMLElement {

        this.setHeading(heading, iconClass);

        try {
            if (typeof content == "object") {
                if (content["node"] && typeof (content["useInnerContent"]) == "boolean" && typeof (content["useAsTargetNode"]) == "boolean") {

                    let config: TargetNodeConfig = <any>content;

                    while (config.node.childNodes.length) {
                        this.setContent(config.node.childNodes[0] as HTMLElement);
                    }

                    this.UiModder.domConstruct.place(this.domNode, config.node);

                } else {
                    this.attachAtNode(content as HTMLElement);
                }

            } else {
                this.setContent(content);
            }
        } catch (ex) {
            this.setContent(content as string);
        }

        if (size) {
            this._setSizeAttr(size);
        }

        if (collapsible === true || collapsible === false) {
            this._isCollapsible = collapsible;
        }

        if (expandable === true || expandable === false) {
            this._isExpandable = expandable;
        }

        if (expanded === true || expanded === false) {
            this._isExpanded = expanded;
        }


        //this.toggleExpand(this.isExpanded, true);
        this.startAnimation(this._isExpanded);
        this._handleButtons();

        if (marking) {
            this.setMarking(marking);
        }

        return this.containerNode;
    }

    /**
     * Entfernt innere Abstände (Padding) im Container. Das Element im Expander nimmt daher die komplette Fläche ein.
     * @param bFlush Abstände entfernen oder wieder hinzufügen
     */
    setFlushContent(bFlush: boolean = true) {
        this.UiModder.domClass.toggle(this.containerNode, "noPadding flush", bFlush == true);
    }

    setCollapsible(collapsible: boolean) {
        this._isCollapsible = collapsible !== false;

        this._handleButtons();
    }

    setExpandable(expandable: boolean) {
        this._isExpandable = expandable !== false;

        this._handleButtons();
    }

    private _currentMarking: string = null;
    /**
     * Setzt eine optische Markierung für die Section, um den Benutzer besser zu führen.
     * @param type Art der Markierung. Je nach Typ wird die Section für den Benutzer anders hervorgehoben.
     */
    setMarking(type: ExpandableSectionMarkings, counter?: number) {
        if (this._destroyed || !this.domNode) return;


        if (typeof (counter) == "number") {
            this.setCounter(counter, type);
            return;
        }

        for (let value in ExpandableSectionMarkings) {
            this.UiModder.domClass.remove(this.domNode, value);
        }



        switch (type) {
            default:
                break;

            case "hidden":
            case "empty":
            case "default":
            case "optional":
            case "checked":
            case "warning":
            case "hasContent":
                this.UiModder.domClass.add(this.domNode, type);
                this._currentMarking = type;
                break;
        }

    }

    /**
     * Stellt ein kleines Zähler-Label in den Expander und ändert die optische Markierung.
     * @param counter Neue Zahl, oder null, um den Zähler zu entfernen
     * @param overrideMarking Falls null: bestehende Markierung bleibt erhalten. Falls nicht angegeben (undefined): Abhängig von counter wird der Expander als default (null), empty (0) oder hasContent (ungleich 0) makiert
     */
    setCounter(counter: number, overrideMarking?: ExpandableSectionMarkings) {
        if (typeof (counter) != "number") {
            this.UiModder.domConstruct.empty(this.counterNode);
            this.hideNode(this.counterNode);
            if (typeof (overrideMarking) == "undefined") {
                this.setMarking(ExpandableSectionMarkings.default);
            } else if (overrideMarking == null) {
                // lassen wies ist
            } else {
                this.setMarking(overrideMarking);
            }
            return;
        }

        this.hideNode(this.counterNode, false);
        this.UiModder.domAttr.set(this.counterNode, "innerHTML", counter.toString());

        if (overrideMarking) {
            this.setMarking(overrideMarking);
        } else if (overrideMarking == null) {
            // lassen wies ist
        } else if (typeof (overrideMarking) == "undefined") {
            // je nach counter:
            this.setMarking(counter == 0 ? ExpandableSectionMarkings.empty : ExpandableSectionMarkings.hasContent);
        }
    }

    /** Ist der Container gerade anklickbar aufgrund von Einstellungen und Zustand? */
    private checkInteraction(): boolean {
        return (this._isExpandable && this._isExpanded != true) || // nicht aufklappbar und gerade zugeklappt
            (this._isCollapsible && this._isExpanded == true); // oder nicht zuklappbar und gerade offen
    }

    private _handleButtons(): void {
        let isInteractive = this.checkInteraction();

        this.hideNode(this.expanderIconS, !isInteractive);
        this.hideNode(this.expanderIconM, !isInteractive);
        this.hideNode(this.expanderIconL, !isInteractive);

        this.UiModder.domClass.toggle(this.headerContainer, "hoverDark clickable", isInteractive);

        this.UiModder.domClass.toggle(this.expanderIconS, "fa-rotate-180", this._isExpanded);
        this.UiModder.domClass.toggle(this.expanderIconM, "fa-rotate-180", this._isExpanded);
        this.UiModder.domClass.toggle(this.expanderIconL, "fa-rotate-180", this._isExpanded);
    }

    private _onClick_Header(): void {

        let target = !this._isExpanded;

        this.toggleExpand(!this._isExpanded);
    }

    toggleExpand(targetState?: boolean, forceState?: boolean, skipAnimation?: boolean) {
        if (this.checkInteraction() != true && forceState != true) {
            return;
        }

        if (targetState !== true && targetState !== false) {
            targetState = !this._isExpanded;
        }

        //this.hideNode(this.mainContainer, !targetState);
        if (targetState != this._isExpanded) {
            if (skipAnimation === true) {
                let bak = this._animationSpeed;
                this._animationSpeed = 0;
                this.startAnimation(targetState);
                setTimeout( lang.hitch(this, function () {
                    this._animationSpeed = bak;
                }), 10);
            } else {
                this.UiModder.domClass.add(this.outerContainer, "animate");

                setTimeout(lang.hitch(this, this.startAnimation, targetState), 10);
            }
            this._isExpanded = targetState;

            this.emit(targetState ? "expand" : "collapse");
        }


        this._handleButtons();
    }

    isExpanded(): boolean {
        return this._isExpanded;
    }
    private startAnimation(targetState: boolean): void {
        if (this._destroyed) return;

        this.UiModder.domClass.toggle(this.domNode, "expanded", targetState);
        this.UiModder.domClass.remove(this.containerNode, "_emptychild");
        // nach Animation Höhe auslesen lassen und die Animation abschließen:
        setTimeout(lang.hitch(this, this.afterAnimation), this._animationSpeed);
    }

    private afterAnimation(): void {
        if (this._destroyed) return;
        if (this._isExpanded) {
        }

        this.UiModder.domClass.remove(this.outerContainer, "animate");
    }
    /**
     * Beschriftung (Text oder Element) setzen. Icon kann gesetzt oder geändert oder mit null zurückgesetzt werden.
     * @param content
     * @param iconClass
     */
    setHeading(content: HTMLElement | string, iconClass?: string) {

        if (content != undefined) {
            this.UiModder.domConstruct.empty(this.headingTxtL);
            this.UiModder.domConstruct.empty(this.headingTxtM);
            this.UiModder.domConstruct.empty(this.headingTxtS);
        }

        // die Icons sollen geändert werden (null = reset, oder irgendein neuer Wert. undefined gilt nicht!)
        if (this.currentHeadingIcons?.length && (iconClass == null || !!iconClass)) {
            for (let icon of this.currentHeadingIcons) this.UiModder.domConstruct.destroy(icon);
        }

        if (typeof( iconClass) == "string") {
            HtmlHelpers.createFontawesomeIcon(iconClass, "right", this.headingTxtL);
            HtmlHelpers.createFontawesomeIcon(iconClass, "right", this.headingTxtM);
            HtmlHelpers.createFontawesomeIcon(iconClass, "right", this.headingTxtS);
        }

        if (typeof (content) == "string") {
            content = TranslationHelpers.tryTranslate(content);
            this._cyText = content;

            this.UiModder.domAttr.set(this.headingTxtS, "innerHTML", content);
            this.UiModder.domAttr.set(this.headingTxtM, "innerHTML", content);
            this.UiModder.domAttr.set(this.headingTxtL, "innerHTML", content);
        } else {
            if (content != null) {
                this.UiModder.domConstruct.place(content.innerHTML, this.headingTxtS);
                this.UiModder.domConstruct.place(content.innerHTML, this.headingTxtM);
                this.UiModder.domConstruct.place(content.innerHTML, this.headingTxtL);

                this.hideNode(content);
            }
        }

        if (this.headerContainer && this._cyText) {
            this.headerContainer.setAttribute("data-cy", "expandableSection:toggle:" + this._cyText);
        }

    }


    /**
     * Entfernt Sonderelemente aus dem Aufklappbalken.
     * Nicht entfernt werden ggf. vorhandene Markierungen, die via SetMarking gesteuert werden.
     */
    emptyHeadingElements() {
        this.UiModder.domConstruct.empty(this.headerElementsContainer);
        //
        this.UiModder.domConstruct.empty(this.iconL);
        this.UiModder.domConstruct.empty(this.iconM);
        this.UiModder.domConstruct.empty(this.iconS);
    }

    addFunctionToMenu(label: string, iconClass: string, callback: Function, position: "menu" | "actionbar", includeInMenu?: boolean) {

    }

    private _setIconAttr(iconClass: string) {
        this.setHeading(undefined, iconClass);
    }

    /**
     * @deprecated Bitte addIconToExpander verwenden!
     * LEGACY-WRAPPER!
     * bitte addIconToExpander verwenden!
     */
    addRightContentIcon(iconClass: string, position: "left" | "right" = "right") {
        this.addIconToExpander(iconClass);
    }

    /**
     * Fügt ein Icon zum Aufklappbalken hinzu.
     */
    addIconToExpander(iconClass: string) {
        if (iconClass) {

           let target = this.iconM;

           switch (this.currentSize) {
               case "small":
                   target = this.iconS;
                   break;
               case "medium":
                   target = this.iconM;
                   break;
               case "large":
                   target = this.iconL;
                   break;
           }

            let icon = HtmlHelpers.createFontawesomeIcon(iconClass, true, target);

        }
    }

    private _buttonsRegistry: Record<string, { name: string, label?: string, callback: Function, enabled: boolean, visible: boolean, node: HTMLElement, iconClass?: string,  }>;

    /**
     * Fügt ein Icon zum Aufklappbalken hinzu.
     */
    addButtonToExpander(internalName: string,
        callback: Function,
        iconClass?: string,
        label?: string,
        styleClass?: string,
        position: "left" | "right" = "right",
        enabled?: boolean) {

        if (!internalName || !callback) {
            return;
        }

        if (!label && !iconClass) label = internalName;

        if (!this._buttonsRegistry) this._buttonsRegistry = {};

        let existingBtn = this._buttonsRegistry[internalName];
        if (existingBtn) {
            // neue position? Dann Button entfernen und neu machen:
            if (position) {
                this.removeButton(internalName);
                existingBtn = null;
            } else {
                
                // oder raus?

                existingBtn.callback = callback;

                if (typeof (!iconClass) != "string") iconClass = existingBtn.iconClass;
                if (typeof (label) != "string") label = existingBtn.label;
            }
        }

        if (!existingBtn){
            let btn = this.UiModder.domConstruct.create("button", {
                className: "buttonBase transparent border"
            });
            btn.setAttribute("data-cy", ["expandableSection", this._cyText, "btn", internalName].filter(Boolean).join(":"));
            this._buttonsRegistry[internalName] = {
                callback: callback,
                enabled: enabled !== false,
                name: internalName,
                node: btn,
                visible: true,
                label: label,
                iconClass: iconClass
            };

            this.addElementToExpander(btn, position);
            on(btn, "click", lang.hitch(this, this._onClickButton, internalName));

            this.addElementToExpander(btn, position);
        }

        let config = this._buttonsRegistry[internalName];
        let node = config.node;


        if (config.label) {
            node.innerHTML = TranslationHelpers.tryTranslate(config.label);
        }

        if (config.iconClass) {
            let icon = this.createFontawesomeIcon(config.iconClass, !!config.label ? "&nbsp;" : "");
            this.UiModder.domConstruct.place(icon, node, "first");
        }

        if (styleClass) {
            this.UiModder.domClass.add(node, styleClass);
        }



        this.disableButton(internalName, config.enabled != true);
    }

    disableButton(internalName: string, bDisable: boolean = true) {
        this.enableButton(internalName, !bDisable);
    }
    enableButton(internalName: string, bEnable: boolean = true) {
        let existingBtn = this._buttonsRegistry[internalName];
        if (existingBtn) {
            this.disableNode(existingBtn.node, bEnable != true);
            existingBtn.enabled = bEnable !== false;
        }
    }
    removeButton(internalName: string) {
        let existingBtn = this._buttonsRegistry[internalName];
        if (existingBtn) {
            this.UiModder.domConstruct.destroy(existingBtn.node);

            this._buttonsRegistry[internalName] = null;
        }
    }

    private _onClickButton(internalName: string, evt: MouseEvent) {
        let existingBtn = this._buttonsRegistry[internalName];
        if (existingBtn.enabled == true && existingBtn.visible == true) {
            if (evt.stopPropagation) evt.stopPropagation();

            existingBtn.callback();
        }
    }
    /**
     * @deprecated Bitte addElementToHeading verwenden!
     * LEGACY-Wrapper!!
     * bitte addElementToHeading verwenden
     */
    addRightHeadingContent(element: HTMLElement | MdsLax<InstanceType<typeof _Widget>>, position: "left" | "right" = "right") {
        this.addElementToExpander(element, position);
    }
    /**
     * Fügt ein beliebiges HTML-Element zum Aufklappbalken dazu.
     * @param element
     * @param position Falls bereits Elemente vorhanden sind, wird das zusätzliche Element links oder rechts platziert.
     */
    addElementToExpander(element: HTMLElement | MdsLax<InstanceType<typeof _Widget>>, position: "left" | "right" = "right") {

        if (element) {

        } else {
            this.emptyHeadingElements();
            return;
        }

        let pos = position === "left" ? "first" : "last";


        let target = this.headerElementsContainer;


        let elemAny: any = element; // Compiler zufriedenstellen...

        if (elemAny.domNode ) {
            //this.UiModder.domClass.add(elemAny.domNode, "w3-margin-left");
            elemAny.placeAt(target, pos);
        } else {
            //this.UiModder.domClass.add(elemAny, "w3-margin-left");
            this.UiModder.domConstruct.place(elemAny, target, pos);
        }
    }

    attachAtNode(node: HTMLElement) {
        if (!node) {
            return;
        }
        let parent = node.parentNode;
        let index = Array.prototype.indexOf.call(parent.childNodes, node);


        if (index != -1) {

            this.setContent(node);
            this.placeAt(parent, index);

            try {
                on(node, "StandbyStart", lang.hitch(this, this._onChildStandbyStart));
                on(node, "StandbyEnd", lang.hitch(this, this._onChildStandbyEnd));
            } catch (ex) {

            }
        }

    }
    setContent(content: HTMLElement | string | MdsLax<InstanceType<typeof _WidgetBase>>) {
        if (typeof (content) == "string") {
            this.UiModder.domAttr.set(this.containerNode, "innerHTML", content);
        } else if (content instanceof _WidgetBase) {
            this.addChild(content, this.getChildren().length);
            return;
        } else {
            //this.placeAt(content.parentNode);
            this.UiModder.domConstruct.place(content, this.containerNode);
        }

        if (content) {
            this.setMarking(ExpandableSectionMarkings.default);

            if (typeof content == "object") {
                try {
                    on(content, "StandbyStart", lang.hitch(this, this._onChildStandbyStart));
                    on(content, "StandbyEnd", lang.hitch(this, this._onChildStandbyEnd));
                } catch (ex) {

                }
            }
        }
    }

    empty(collapse?: boolean, newMarking?: ExpandableSectionMarkings) {
        this.UiModder.domConstruct.empty(this.containerNode);

        if (newMarking) this.setMarking(newMarking);
        if (collapse) this.toggleExpand(false);
    }

    private _setHeadingAttr(heading: string) {
        this.setHeading(heading);
    }
    private currentSize: string = "medium";

    private _setSizeAttr(size: string) {
        switch (size) {
            case "large":
            case "medium":
            case "small":
                break;

            default:
                size = "medium";
                break;
        }

        this.UiModder.domClass.remove(this.domNode, "large medium small");

        this.UiModder.domClass.add(this.domNode, size);

        this.currentSize = size;
    }
    private _setMarkingAttr(marking: ExpandableSectionMarkings) {
        this.setMarking(marking);
    }
    private _setExpandedAttr(expanded: boolean) {
        this.toggleExpand(expanded === true);
    }

    private _setIsExpandable(bool) {
        this.setExpandable(bool);
    } private _setIsCollapsible(bool) {
        this.setCollapsible(bool);
    } private _setIsExpanded(bool) {
        this.toggleExpand(bool === true, true);
    }

    private _extendChildren() {
        let children = this.getChildren();

        for (let child of children) {
            (child as _WidgetBaseSPWithExpander).expander = this;

            try {
                child.on("StandbyStart", lang.hitch(this, this._onChildStandbyStart));
                child.on("StandbyEnd", lang.hitch(this, this._onChildStandbyEnd));
            } catch (ex) {

            }
        }

    }


    postMixInProperties() {
        super.postMixInProperties.apply(this, arguments as any); // standard code aus basisklasse
    }

    postCreate(): void {
        super.postCreate.apply(this, arguments as any);

        this._extendChildren();
        this._handleButtons();

        AccessibilityLabelsHelper.InputLabelledbyBind([
            {
                labelElement: this.headingTxtL, labelIdValue: "TxtLLabel"
            }, {
                labelElement: this.headingTxtM, labelIdValue: "TxtMLabel"
            }, {
                labelElement: this.headingTxtS, labelIdValue: "TxtSLabel"
            }],
            this.outerContainer,
            this.id);
    }

    addChild(widget, insertIndex): void {
        super.addChild.apply(this, arguments as any);
        try {
            widget.on("StandbyStart", lang.hitch(this, this._onChildStandbyStart));
            widget.on("StandbyEnd", lang.hitch(this, this._onChildStandbyEnd));
            (widget as _WidgetBaseSPWithExpander).expander = this;
        } catch (ex) {

        }

        this.setMarking(ExpandableSectionMarkings.default);
    }

    private _onChildStandbyStart() {
        this.StandbyStart(null); // allgemeine busy-Animation anzeigen, keine bestimmte Node.
        if (this._standbyEndDelay != null) {
            clearTimeout(this._standbyEndDelay);
            this._standbyEndDelay = null;
        }
    }

    private _standbyEndDelay: any = null;
    private _onChildStandbyEnd() {
        if (this._standbyEndDelay != null) {
            clearTimeout(this._standbyEndDelay);
            this._standbyEndDelay = null;
        }
        this._standbyEndDelay = setTimeout(lang.hitch(this, this.StandbyStop), 400);
        //this.StandbyStop(); // allgemeine busy-Animation anzeigen, keine bestimmte Node.
    }



    StandbyStart(node?: HTMLElement, stepName?: string, stepLabel?: string, stepCallback?: Function, groupName?: string): any {

        let result = null;
        if (node) {
            result = super.StandbyStart.apply(this, arguments as any);
        } else {
            if (node == null) {

            } else if (typeof node == "undefined") {
                result = this.StandbyStart(this.containerNode);
            }

        }

        this.UiModder.domClass.add(this.progressContainer, "ego_animated_progressBar");

        return result;
    }

    StandbyStop(stepname?: string, groupName?: string) {
        super.StandbyStop.apply(this, arguments as any);

        this._standbyEndDelay = null;

        if (this.progressContainer){
            this.UiModder.domClass.remove(this.progressContainer, "ego_animated_progressBar");
        }
    }

    buildRendering(): void {
        super.buildRendering.apply(this, arguments as any);

        if (this.hasChildren()) {
            this._extendChildren();
            this.setMarking(ExpandableSectionMarkings.default);
        } else if (this.containerNode.firstChild) {
            this.setMarking(ExpandableSectionMarkings.default);
        }
    }

    startup(): void {
        super.startup.apply(this, arguments as any);

        if (this.hasChildren()) {
            this._extendChildren();
            if (!this._currentMarking) {
                this.setMarking(ExpandableSectionMarkings.default);
            }
        } else if (this.containerNode.firstChild) {
            if (!this._currentMarking) {
                this.setMarking(ExpandableSectionMarkings.default);
            }
        }

    }
}

export default mdsWidget(ExpandableSection);
